import React from "react";
import { Plus, X } from "lucide-react";
import {
  Button,
  CustomDropdown,
  TextField,
  TrashIcon,
  controlBoxClasses,
  fieldLabelClasses,
} from "../../../components/ui";
import {
  DAY_FULL,
  DAY_SHORT,
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
  PERIOD_OPTIONS,
  WHEN_OPTIONS,
  toClockLabelFromParts,
  toClockParts,
} from "../data/supplementOptions";
import SupplementSummary from "./SupplementSummary";
import { createDose, isSupplementComplete, numericDose } from "../supplementMapping";
import type { GateProps } from "../../billing/hooks/useSubscriptionGate";
import type { MealSlotTypeOption } from "../../mealPlans/types/plans.types";
import type {
  DoseWhen,
  SupplementDose,
  SupplementDraft,
  SupplementFrequency,
  SupplementUnitOption,
} from "../types/supplements.types";

const toOptions = (values: string[]) =>
  values.map((value) => ({ value, label: value }));

const WHENS = toOptions(WHEN_OPTIONS);
const HOURS = toOptions(HOUR_OPTIONS);
const MINUTES = toOptions(MINUTE_OPTIONS);
const PERIODS = toOptions(PERIOD_OPTIONS);

const FREQUENCIES: Array<{ value: SupplementFrequency; label: string }> = [
  { value: "everyday", label: "Everyday" },
  { value: "days", label: "Chosen days" },
];

/**
 * The three columns a dose row is laid out in, shared with its headings. Time
 * is the widest: it holds three lists side by side, and each has to show a
 * two-digit figure and its chevron without one squeezing the other.
 */
const DOSE_GRID =
  "grid grid-cols-[28px_minmax(0,1fr)_minmax(0,1.3fr)_minmax(0,1.3fr)_28px] items-center gap-3 sm:gap-4";

interface DoseRowProps {
  dose: SupplementDose;
  /** Which dose of the day this is — the number in the circle. */
  index: number;
  /** Names the fields to a screen reader: "When for dose 2 of Whey Protein". */
  owner: string;
  /** `catalog.mealSlotTypes` — what a dose can be timed against. */
  meals: MealSlotTypeOption[];
  onChange: (dose: SupplementDose) => void;
  /**
   * Unset on the last row standing. A supplement with no doses is a schedule
   * with no times in it, and there would be no way back to having one but the
   * add button — so the bin goes rather than emptying the list.
   */
  onRemove?: () => void;
}

const DoseRow: React.FC<DoseRowProps> = ({
  dose,
  index,
  owner,
  meals,
  onChange,
  onRemove,
}) => {
  const position = index + 1;

  /**
   * What the Meal field offers: the catalog, plus whatever this dose already
   * names if the catalog does not.
   *
   * A plan stored against a meal that has since left the list would otherwise
   * open showing a blank where its meal is, which reads as a dose that never
   * had one.
   */
  const mealOptions = React.useMemo(() => {
    const names = meals.map((meal) => meal.name);
    const held = dose.meal.trim();

    return toOptions(held && !names.includes(held) ? [held, ...names] : names);
  }, [meals, dose.meal]);

  const clock = toClockParts(dose.time);

  /**
   * What the Minute list offers: every five minutes, plus whatever minute this
   * dose already carries if that is not one of them. A plan the endpoint
   * stored at 08:17 would otherwise open with a blank where its minute is —
   * the same care the Meal list takes with a meal that has left the catalog.
   */
  const minuteOptions = React.useMemo(
    () =>
      clock.minute && !MINUTE_OPTIONS.includes(clock.minute)
        ? toOptions([clock.minute, ...MINUTE_OPTIONS])
        : MINUTES,
    [clock.minute]
  );

  // One part changed, the whole label written back.
  const setClock = (part: Partial<typeof clock>) =>
    onChange({ ...dose, time: toClockLabelFromParts({ ...clock, ...part }) });

  return (
    <div className={DOSE_GRID}>
      {/* The dose's place in the day, which is what the rows are ordered by.
          Decorative: the fields beside it carry the number in their own names,
          so a screen reader is not read a bare digit. */}
      <span
        aria-hidden="true"
        className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#EFFBFD] text-[12px] font-medium text-[#00424B]"
      >
        {position}
      </span>

      {/* Every list on the row opens on its placeholder — the row is the
          dietitian's to write, and a time nobody picked is not one a client
          should be told to keep. */}
      <CustomDropdown
        options={WHENS}
        value={dose.when}
        placeholder="Select"
        onChange={(when) => onChange({ ...dose, when: when as DoseWhen })}
        heading="When"
        aria-label={`When for dose ${position} of ${owner}`}
      />

      <CustomDropdown
        options={mealOptions}
        value={dose.meal}
        placeholder="Select meal"
        // The id travels with the label, because a write sends
        // `meal_slot_type` rather than the name — so the dose holds the
        // catalog row and not the word. A meal the catalog does not name
        // leaves it null, which `isSupplementComplete` will not let be saved.
        onChange={(meal) =>
          onChange({
            ...dose,
            meal,
            mealSlotTypeId: meals.find((row) => row.name === meal)?.id ?? null,
          })
        }
        heading="Meal"
        aria-label={`Meal for dose ${position} of ${owner}`}
      />

      {/* The time, as an hour, a minute and a half of the day. Three lists
          rather than one because the one ran to forty-eight rows and still
          could not say twenty past. The card keeps holding the one label —
          `"08:00 am"` — so the summary and the wire read it as they did; the
          lists are only a different way of writing it. */}
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-2">
        <CustomDropdown
          options={HOURS}
          value={clock.hour}
          placeholder="hr"
          onChange={(hour) => setClock({ hour })}
          heading="Hour"
          aria-label={`Hour for dose ${position} of ${owner}`}
        />
        <CustomDropdown
          options={minuteOptions}
          value={clock.minute}
          placeholder="min"
          onChange={(minute) => setClock({ minute })}
          heading="Minute"
          aria-label={`Minute for dose ${position} of ${owner}`}
        />
        <CustomDropdown
          options={PERIODS}
          value={clock.period}
          placeholder="am/pm"
          onChange={(period) => setClock({ period })}
          heading="AM or PM"
          aria-label={`AM or PM for dose ${position} of ${owner}`}
        />
      </div>

      {/* Kept in the layout when there is nothing to press, so the boxes above
          and below it stay in one column — see `onRemove`. */}
      <div className="flex justify-center">
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove dose ${position} of ${owner}`}
            className="rounded text-[#E5484D] transition-colors hover:text-[#C62A2F] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export interface SupplementCardProps {
  draft: SupplementDraft;
  /** Which supplement of the sitting this is — the heading's number. */
  index: number;
  /**
   * `GET /api/v1/supplement-units/`, fetched once for the whole page rather
   * than once per card — see `CreateSupplementPage`. Empty while it is in
   * flight, or if it failed: the field then offers whatever unit the card
   * already carries and nothing else, which for a fetched plan is the one it
   * was stored with.
   */
  units: SupplementUnitOption[];
  /**
   * `GET /api/v1/meal-slot-types/`, fetched once for the whole page the same
   * way. A dose is timed against one of these, and a write sends the row's id
   * — so a card cannot be saved until the catalog has answered.
   */
  meals: MealSlotTypeOption[];
  onChange: (draft: SupplementDraft) => void;
  /**
   * Stores the card. A request rather than a flag flipped — the plan is
   * written the moment a card is saved, the way a meal option's rows are —
   * which is what `saving` reports the progress of.
   */
  onSave: () => void;
  /**
   * Whether that request is out. Save spins and takes no second press while
   * it is; the fields stay as they are, since what is typed into them is
   * still the dietitian's until the server says otherwise.
   */
  saving?: boolean;
  /**
   * Whether the workspace may write at all — see `useSubscriptionGate`. Save
   * carries the reason rather than storing something the server would refuse
   * anyway. The fields above it are left alone: what is typed into a card goes
   * nowhere until Save. The same arrangement `MealOptionCard` makes.
   */
  gate?: GateProps;
  /**
   * Unset on the last card standing, for the same reason a dose row's bin is:
   * a screen with no supplement on it has nothing to fill in.
   */
  onRemove?: () => void;
  /**
   * Whether the card is only to be read — a plan the client already has, which
   * this screen does not offer to change under them. Modify and the bin go
   * rather than greying out, the way a sent meal plan's do. Only a saved card
   * is ever read-only: the form has nothing to be read-only about.
   */
  readOnly?: boolean;
}

/**
 * One supplement being written: what it is, how much of it, how often, and at
 * what times of day.
 *
 * The card carries its own Save because the screen writes several at once and
 * they are finished one at a time — saving each as it is done is what lets a
 * dietitian work down the page without holding every answer in their head. The
 * page's own Save & send is what sends the lot.
 *
 * Saving turns it into something to read — see `SupplementSummary` — and
 * Modify turns it back. The same pair the meal plan builder makes of an
 * option, so the two halves of a plan are written the same way.
 */
const SupplementCard: React.FC<SupplementCardProps> = ({
  draft,
  index,
  units,
  meals,
  onChange,
  onSave,
  saving = false,
  onRemove,
  gate = { disabled: false },
  readOnly = false,
}) => {
  const position = index + 1;
  // What the fields call this supplement to a screen reader. Its own name once
  // it has one, and its place on the page until then.
  const owner = draft.name.trim() || `supplement ${position}`;

  /**
   * What the Unit field offers.
   *
   * The catalog, plus whatever the card already holds if the catalog does not
   * name it — a plan stored against a unit that has since left the list would
   * otherwise open showing a blank where its unit is, which reads as a plan
   * that never had one.
   */
  const unitOptions = React.useMemo(() => {
    const names = units.map((unit) => unit.name);
    const held = draft.unit.trim();

    return toOptions(
      held && !names.includes(held) ? [held, ...names] : names
    );
  }, [units, draft.unit]);

  const set = <K extends keyof SupplementDraft>(
    key: K,
    value: SupplementDraft[K]
  ) =>
    // Any edit unsaves the card: what is on screen is no longer what was
    // stored, and a Save that stayed quiet would say it was.
    onChange({ ...draft, [key]: value, saved: false });

  const toggleDay = (day: number) =>
    onChange({
      ...draft,
      // Naming days is choosing them, so the frequency follows the toggles
      // rather than having to be set first — and the first day named is the
      // first day chosen, not the one left off a week that was all on.
      days:
        draft.frequency === "everyday"
          ? [day]
          : draft.days.includes(day)
          ? draft.days.filter((current) => current !== day)
          : [...draft.days, day],
      frequency: "days",
      saved: false,
    });

  // Whether the note box is showing. Open from the start on a card that has
  // one, so a supplement reopened to be modified does not hide what was
  // written on it behind a toggle nobody would think to press.
  const [noting, setNoting] = React.useState(() => !!draft.note.trim());

  const setDose = (dose: SupplementDose) =>
    set(
      "doses",
      draft.doses.map((current) => (current.id === dose.id ? dose : current))
    );

  return (
    // The card itself, and the tinted panel inside it, are a shade apart: the
    // outer one lifts the card off the page, the inner one holds the row of
    // white controls together. Both take their own two values rather than the
    // shared `border-line`, since both set a half-pixel rule.
    // Named, so a page of several is navigable by landmark rather than by
    // scrolling — and so that each card's own answers can be read back against
    // the supplement they belong to. Its own name once it has one, and its
    // place on the page until then, which is what every field here is labelled
    // by too.
    <section
      aria-label={owner}
      className="rounded-md border-[0.5px] border-[#E2E2E2] bg-[#FCFCFD] px-5 sm:px-6 py-5"
    >
      {/* "Supplement 1" is what a card with nothing in it can be called. Once
          it has a name it is called that, in the summary's own heading — so
          the numbered one goes rather than sitting above it saying less. */}
      {!draft.saved && (
        <h2 className="text-[15px] 3xl:text-[16px] font-medium text-ink">
          Supplement {position}
        </h2>
      )}

      {draft.saved ? (
        <SupplementSummary
          draft={draft}
          index={index}
          // Modify is the one thing that makes a saved card writable again,
          // which is what keeps a read card from being changed by a stray
          // click on a control it no longer draws.
          onModify={
            readOnly ? undefined : () => onChange({ ...draft, saved: false })
          }
          onRemove={readOnly ? undefined : onRemove}
        />
      ) : (
        // Tinted and hairline-ruled, so the fields — which are white — read as
        // sitting on the card rather than being drawn straight onto it. Its
        // own two values rather than the shared `border-line`: this is the
        // only surface in the app that sets a half-pixel rule, and
        // `SupplementSummary` draws the same box for the read view.
        <div className="mt-4 rounded-md border-[0.5px] border-[#E2E2E2] bg-[#FAFAFA] px-4 sm:px-5 py-5">
        {/* What it is, and how much of it. */}
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="min-w-0 flex-1">
            <TextField
              label="Supplement name"
              value={draft.name}
              onValueChange={(name) => set("name", name)}
              placeholder="eg Whey protein"
              maxLength={80}
            />
          </div>
          {/* How much of it, in one control: the figure typed on the left and
              what it is counted in picked on the right. Two fields on the
              wire still — `dosage` and the unit's catalog id — but one answer
              to give, since neither half means anything without the other. */}
          <div className="w-full lg:w-[260px] 3xl:w-[300px]">
            <div className="mb-1.5 flex items-center gap-1.5">
              <label htmlFor={`${draft.id}-dose`} className={fieldLabelClasses}>
                Dose
              </label>
              {/* What the figure is counted against — one intake, not a day.
                  Outside the `<label>` so it does not become part of the
                  field's name, and announced as its description instead. */}
              <span
                id={`${draft.id}-dose-hint`}
                className="text-[12px] 3xl:text-[13px] text-ink-muted"
              >
                per intake
              </span>
            </div>
            {/* The form kit's own box, drawn around the pair — so the field
                sits at the height every other control in the row does, and
                the border follows the caret wherever in it focus lands. */}
            <div className={controlBoxClasses()}>
              <input
                id={`${draft.id}-dose`}
                type="text"
                // A figure alone: the unit is the control beside this one,
                // and letters here would be it written twice. Text rather
                // than `type="number"`, which cannot hold a plain "0." while
                // it is being typed and spins on the wheel. See `numericDose`.
                inputMode="decimal"
                value={draft.dosage}
                onChange={(event) =>
                  set("dosage", numericDose(event.target.value))
                }
                placeholder="eg 2"
                maxLength={20}
                aria-describedby={`${draft.id}-dose-hint`}
                className="w-16 min-w-0 flex-shrink-0 bg-transparent text-inherit placeholder:text-ink-subtle focus:outline-none"
              />

              {/* A hairline between the two halves, so the figure and its unit
                  read as two answers rather than one run-on box. Its margins
                  plus the box's own gap come to the box's side padding, so
                  the figure sits as far from the rule as it does from the
                  frame, and the unit the same on its side. */}
              <span
                aria-hidden="true"
                className="mx-2 h-5 w-px flex-shrink-0 bg-line"
              />

              <CustomDropdown
                bare
                className="min-w-0 flex-1"
                options={unitOptions}
                value={draft.unit}
                // The id travels with the name, so the card holds the catalog
                // row rather than the word — which is what a write will send
                // once there is one. A unit off the list the catalog does not
                // name leaves it null, the same as a card that has none.
                onChange={(unit) =>
                  onChange({
                    ...draft,
                    unit,
                    unitId: units.find((row) => row.name === unit)?.id ?? null,
                    saved: false,
                  })
                }
                heading="Unit"
                // The half has no label of its own — "Dose" names the pair —
                // so the field says what it is picking for itself.
                aria-label="Unit"
              />
            </div>
          </div>
        </div>

        {/* How often. */}
        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="w-full sm:w-[220px] 3xl:w-[250px]">
            <label
              id={`${draft.id}-frequency`}
              className={`${fieldLabelClasses} mb-1.5`}
            >
              Frequency
            </label>
            <CustomDropdown
              options={FREQUENCIES}
              value={draft.frequency}
              onChange={(frequency) =>
                onChange({
                  ...draft,
                  frequency: frequency as SupplementFrequency,
                  // Going back to every day ticks them all, so the toggles
                  // never disagree with the word above them. Going the other
                  // way clears them: chosen days start with none chosen, and
                  // the week is picked out rather than pared down.
                  days:
                    frequency === "everyday"
                      ? [0, 1, 2, 3, 4, 5, 6]
                      : draft.frequency === "everyday"
                      ? []
                      : draft.days,
                  saved: false,
                })
              }
              heading="Frequency"
              aria-labelledby={`${draft.id}-frequency`}
            />
          </div>

          <fieldset className="min-w-0">
            <legend className={`${fieldLabelClasses} mb-1.5`}>
              Choose days
            </legend>
            <div className="flex flex-wrap gap-2">
              {DAY_SHORT.map((short, day) => {
                const on = draft.days.includes(day);
                return (
                  <button
                    key={DAY_FULL[day]}
                    type="button"
                    // "Mo" is not a day read aloud, so the whole name is
                    // what the control is called.
                    aria-label={DAY_FULL[day]}
                    aria-pressed={on}
                    onClick={() => toggleDay(day)}
                    className={`h-10 min-w-[44px] rounded-[6px] border px-2 text-[13px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${
                      on
                        ? "border-[#88E2ED] bg-[#EFFBFD] font-medium text-[#00424B]"
                        : "border-line bg-[#F1F3F4] text-ink-muted hover:text-ink"
                    }`}
                  >
                    {short}
                  </button>
                );
              })}
            </div>
          </fieldset>
        </div>

        {/* And when. */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-[12px] 3xl:text-[13px] font-medium uppercase tracking-wide text-ink-muted">
            Times per day
          </h3>
          <p className="text-[13px] text-ink-muted">Add a row for each dose</p>
        </div>

        {/* The column headings, laid out on the same grid as the rows so they
            sit over the right boxes however the columns resolve. */}
        <div className={`${DOSE_GRID} mt-3`} aria-hidden="true">
          <span />
          <span className={fieldLabelClasses}>When</span>
          <span className={fieldLabelClasses}>Meal</span>
          <span className={fieldLabelClasses}>Time</span>
          <span />
        </div>

        <div className="mt-2 flex flex-col gap-3">
          {draft.doses.map((dose, doseIndex) => (
            <DoseRow
              key={dose.id}
              dose={dose}
              index={doseIndex}
              owner={owner}
              meals={meals}
              onChange={setDose}
              onRemove={
                draft.doses.length > 1
                  ? () =>
                      set(
                        "doses",
                        draft.doses.filter((current) => current.id !== dose.id)
                      )
                  : undefined
              }
            />
          ))}
        </div>

        <div className="mt-3">
          <button
            type="button"
            onClick={() => set("doses", [...draft.doses, createDose()])}
            aria-label={`Add another dose to ${owner}`}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-brand text-brand transition-colors hover:bg-[#E6FAFB] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {/* Anything the schedule above does not say. Behind a toggle because
            most supplements need none, and a box open on every card would read
            as a question every card has to answer. */}
        <div className="mt-4">
          {/* The opener on the left and the way out at the far right, level
              with it — so the row reads as the whole of the section rather
              than as one control with something stuck beside it. */}
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              // Opens only. Closing is the × at the other end, which does more
              // than hide the box — so one control cannot mean both.
              onClick={() => setNoting(true)}
              aria-expanded={noting}
              aria-controls={`${draft.id}-note`}
              className="inline-flex items-center gap-1.5 rounded text-[14px] text-ink transition-colors hover:text-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
            >
              <Plus aria-hidden="true" className="h-4 w-4" />
              Notes
            </button>

            {noting && (
              <button
                type="button"
                // Closing takes the note with it, which is what keeps the two
                // states honest: a box that is open is a supplement with a
                // note on it, and a closed one is a supplement without. A note
                // left behind a shut box would still be read back on the saved
                // card, which is the one thing nobody would expect.
                //
                // Named for what it does rather than for the glyph, so it is
                // not pressed as a plain dismiss.
                onClick={() => {
                  set("note", "");
                  setNoting(false);
                }}
                aria-label={`Remove notes from ${owner}`}
                className="rounded p-1 text-ink-subtle transition-colors hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </button>
            )}
          </div>

          {noting && (
            <textarea
              id={`${draft.id}-note`}
              value={draft.note}
              onChange={(event) => set("note", event.target.value)}
              placeholder="Take supplement with water."
              rows={3}
              maxLength={500}
              aria-label={`Notes for ${owner}`}
              autoFocus
              className="mt-3 w-full resize-y rounded-[6px] border border-line bg-white px-3.5 py-2.5 text-[14px] text-ink placeholder:text-ink-subtle focus:border-brand focus:outline-none"
            />
          )}
        </div>

        <div className="mt-5 flex items-center gap-4 border-t border-line pt-5">
          <Button
            size="md"
            onClick={onSave}
            loading={saving}
            // Nothing to store until the card is whole — a name, a dose and a
            // day to take it on. That one stays plainly disabled: the empty
            // boxes above it are the reason, and they are already on screen. A
            // workspace with no plan behind it gets the reason it cannot see
            // instead. The same pair `MealOptionCard`'s Save makes.
            {...(isSupplementComplete(draft) ? gate : { disabled: true })}
          >
            Save
          </Button>

          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove ${owner}`}
              className="rounded text-ink-subtle transition-colors hover:text-[#E5484D] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
          </div>
        </div>
      )}
    </section>
  );
};

export default SupplementCard;
